import { useState } from 'react';
import { FileText, Loader2, RefreshCw } from 'lucide-react';
import client, { apiError } from '../adminApi';

const DRAFT_FIELDS = [
  ['summary', 'Short summary'],
  ['overview', 'Project overview'],
  ['challenge', 'Challenge'],
  ['solution', 'What Amden implemented'],
];

// "Draft from rough notes" — case study editor only. The AI drafts a first
// pass of summary/overview/challenge/solution from pasted notes; outcomes
// and metrics are never drafted (they stay manual and client-verified).
export const AiDraftPanel = ({ resource, doc, onApply }) => {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  if (resource !== 'case-studies') return null;

  const draft = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await client.post('/admin/ai/case-study-draft', { notes, doc });
      setResult(res.data);
    } catch (err) {
      setError(apiError(err, 'AI drafting is unavailable right now.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ai-draft-panel">
      <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
        <FileText className="h-3.5 w-3.5" aria-hidden="true" /> AI First-Pass Draft
      </p>
      <textarea
        rows={4}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Paste rough notes here — who the client is, what was broken, what you did…"
        aria-label="Rough notes for AI draft"
        data-testid="ai-draft-notes"
        className="mt-2 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm text-navy-900 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900"
      />
      <button
        type="button"
        onClick={draft}
        disabled={busy || notes.trim().length < 20}
        data-testid="ai-draft-generate"
        className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-navy-900 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-navy-700 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />}
        {busy ? 'Drafting…' : result ? 'Draft Again' : 'Draft from Notes'}
      </button>

      {error && <p role="alert" data-testid="ai-draft-error" className="mt-2 text-xs font-semibold text-red-700">{error}</p>}

      {result && (
        <div className="mt-4 space-y-3">
          {DRAFT_FIELDS.filter(([key]) => result[key]).map(([key, label]) => (
            <div key={key}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
              <p className="mt-1 max-h-28 overflow-y-auto whitespace-pre-line rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs leading-relaxed text-slate-700" data-testid={`ai-draft-${key}`}>
                {result[key]}
              </p>
              <button
                type="button"
                onClick={() => onApply(key, result[key])}
                data-testid={`ai-draft-apply-${key}`}
                className="mt-1 rounded border border-slate-300 px-2.5 py-1 text-xs font-bold text-navy-900 hover:bg-navy-50"
              >
                Apply to {label.toLowerCase()}
              </button>
            </div>
          ))}
          <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-700">
            First-pass draft — verify every fact with the client. Outcomes &amp; metrics are never AI-drafted.
          </p>
        </div>
      )}
    </div>
  );
};
